import type { LineAsset, LineAssets } from '../../../../arcticapi/types';
import { LinkSelect } from '../../../base/LinkSelect/LinkSelect';
import styles from './TimetableAssets.module.css';

export interface TimetableAssetsProps {
  assets: LineAssets;
  placeholder?: string;
}

const CATEGORY_LABELS: Record<keyof LineAssets, string> = {
  timetable: 'Timetable',
  map: 'Map',
  fares: 'Fares',
};

/** Drop down list of timetable asset PDFs, grouped by category (timetable, map, fares) */
export function TimetableAssets({
  assets,
  placeholder = 'Select a PDF to download',
}: TimetableAssetsProps) {
  // Put them in the right groups, with labels, and filter out any groups that have no assets
  const groups = (
    Object.entries(CATEGORY_LABELS) as Array<[keyof LineAssets, string]>
  )
    .map(([category, label]) => ({
      label,
      options: assets[category].map((asset) => ({
        href: asset.href,
        label: formatAssetLabel(asset),
      })),
    }))
    .filter((group) => group.options.length > 0);

  if (groups.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <LinkSelect
        aria-label={placeholder}
        groups={groups}
        placeholder={placeholder}
      />
    </div>
  );
}

function formatAssetLabel(asset: LineAsset): string {
  return `${asset.title} (From ${formatEffectiveFrom(asset.effectiveFrom)})`;
}

// TODO: this can move to generic date formatter utility if we need it elsewhere
function formatEffectiveFrom(value: string): string {
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);

  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return formatDate(
      new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))),
    );
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return formatDate(parsed);
}

function formatDate(date: Date): string {
  const weekday = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    timeZone: 'UTC',
  }).format(date);
  const month = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    timeZone: 'UTC',
  }).format(date);
  const year = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
  const day = new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);

  return `${weekday} ${day}${toOrdinalSuffix(Number(day))} ${month} ${year}`;
}

/** Converts a number to its ordinal suffix (1st, 2nd, 3rd, etc) */
function toOrdinalSuffix(day: number): string {
  const remainder = day % 100;

  if (remainder >= 11 && remainder <= 13) {
    return 'th';
  }

  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}
